import { EncryptJWT, exportPKCS8, exportSPKI, importPKCS8, importSPKI, jwtDecrypt, jwtVerify, SignJWT, generateKeyPair } from 'jose'
import type { CryptoKey, JWTPayload } from 'jose'
import chalk from 'chalk'
import { ErrorAumentado } from './errorAumentado'
import { dayjs } from './fechas'
import _ from 'lodash'

// Polyfill for structuredClone
if (typeof globalThis.structuredClone !== 'function') {
  globalThis.structuredClone = function structuredClone(value) {
    return JSON.parse(JSON.stringify(value))
  }
}

export const algFirma = 'RS256'
export const algEncriptacion = 'RSA-OAEP-256'
const encEncriptacion = 'A256GCM'

const cripto = {
  async crearKeysFirmas(): Promise<{
        publica: CryptoKey,
        privada: CryptoKey
    }> {
    const fx = 'Cripto>crearKeysFirmas'
    try {
      // consolo.log(fx)
      const { publicKey: publica, privateKey: privada } = await generateKeyPair(algFirma)
      // consolo.log(fx, { publica, privada })
      return { publica, privada }
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  },

  async crearKeysEncriptacion() {
    const fx = 'Cripto>crearKeysEncriptacion'
    try {
      // consolo.log(fx)
      const { publicKey: publica, privateKey: privada } = await generateKeyPair(algEncriptacion)
      return { publica, privada }
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  },

  exportar: {
    firma: {
      async publica(llave: CryptoKey) {
        return await exportSPKI(llave)
      },
      async privada(llave: CryptoKey) {
        return await exportPKCS8(llave)
      }
    },
    encriptacion: {
      async publica(llave: CryptoKey) {
        return await exportSPKI(llave)
      },
      async privada(llave: CryptoKey) {
        return await exportPKCS8(llave)
      }
    }
  },
  importar: {
    firma: {
      async publica(spki: string) {
        if (!spki || typeof spki !== 'string') {
          throw new Error(`SPKI inválido: ${typeof spki}, longitud: ${spki?.length}`)
        }
        return await importSPKI(spki, algFirma, { extractable: true })
      },
      async privada(pkcs8: string) {
        return await importPKCS8(pkcs8, algFirma, { extractable: true })
      }
    },
    encriptacion: {
      async publica(spki: string) {
        return await importSPKI(spki, algEncriptacion, {
          extractable: true
        })
      },
      async privada(pkcs8: string) {
        return await importPKCS8(pkcs8, algEncriptacion, {
          extractable: true
        })
      }
    }
  },

  async encriptar(llavePublica: CryptoKey, json: JWTPayload): Promise<string> {
    const fx = 'Cripto>encriptar'
    // consolo.log(fx)
    try {
      const encriptado = await new EncryptJWT(json)
        .setProtectedHeader({ alg: algEncriptacion, enc: encEncriptacion })
        .encrypt(llavePublica)
      return encriptado
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  },
  async desencriptar(llavePrivada: CryptoKey, JWTString: string) {
    const fx = 'Cripto>cripto.desencriptar'
    try {
      const { payload, protectedHeader } = await jwtDecrypt(JWTString, llavePrivada)
      return { payload, protectedHeader }
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  },

  async firmar(llavePrivada: CryptoKey, cuerpo: JWTPayload, issuer: string) {
    const fx = 'Cripto>firmar'
    try {
      const token: string = await new SignJWT(cuerpo)
        .setProtectedHeader({ alg: algFirma })
        .setIssuedAt(dayjs().subtract(10, 's').unix())
        .setIssuer(cuerpo.iss || issuer)
        .sign(llavePrivada)
      // consolo.log(fx, jwt)
      return token
    }
    catch (e) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      console.error(chalk.bgRed(fx), e)
      throw new ErrorAumentado(fx, { error: e })
    }
  },

  async verificar(llavePublica: CryptoKey, token: string, { permitirExpirado = false }: { permitirExpirado?: boolean } = {}) {
    const fx = 'Cripto>verificar'
    try {
      const { payload } = await jwtVerify(token, llavePublica, {
        algorithms: [algFirma]
      })
      return payload
    }
    catch (e: unknown) {
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)

      if (_.isObject(e) && 'code' in e) {
        if (e.code === 'ERR_JWS_SIGNATURE_VERIFICATION_FAILED') {
          throw new ErrorAumentado('firmaInvalida', {
            respuesta: {
              code: 401,
              codigo: 'firmaInvalida',
              datos: {
                codigoInterno: e.code,
                token
              }
            }
          })
        }
        if (e.code === 'ERR_JWT_EXPIRED') {
          // Si permitirExpirado es true, extraemos el payload del error y lo retornamos
          // La firma ya fue verificada, solo el claim 'exp' falló
          if (permitirExpirado) {
            const payload = _.get(e, 'payload') as JWTPayload | undefined
            if (payload) {
              const expiredAt = _.get(e, 'expiredAt')
              let expiradoHace: string | undefined
              if (expiredAt) {
                expiradoHace = dayjs(expiredAt).fromNow()
              }
              console.log(chalk.yellow(fx), 'Token expirado permitido, payload extraído, expirado hace:', expiradoHace || 'desconocido')
              return payload
            }
          }

          const expiredAt = _.get(e, 'expiredAt')
          let expiradoHace: string | undefined
          if (expiredAt) {
            expiradoHace = dayjs(expiredAt).fromNow()
          }
          throw new ErrorAumentado('tokenExpirado', {
            error: e,
            datos: { expiradoHace },
            respuesta: {
              code: 401,
              codigo: 'tokenExpirado',
              datos: {
                codigoInterno: e.code,
                token
              }
            }
          })
        }
      }

      console.error(chalk.bgRed(fx), e)

      throw new ErrorAumentado(fx, { error: e })
    }
  }
}

export default cripto
